// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest } from 'rxjs';

// --- HS Code
import {
  ResultsStatuses,
  ResultsViews,
  ScoreModel,
  ScoreModels,
  SurveyResultsModel,
  SurveyStatuses,
  SurveySummaryModel,
  TrackMyJourneyResultsModel,
} from '../../../models';
import { SurveyBaseService } from '../survey/survey-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class CompareReportBaseService implements OnDestroy {
  protected _surveyResults$: BehaviorSubject<TrackMyJourneyResultsModel[] | null>;
  get surveyResults$(): Observable<TrackMyJourneyResultsModel[] | null> {
    return this._surveyResults$;
  }

  protected _widgetDisplayMode$: BehaviorSubject<ResultsViews>;
  get widgetDisplayMode$(): Observable<ResultsViews> {
    return this._widgetDisplayMode$;
  }

  protected _areResultsLoading$: BehaviorSubject<boolean>;
  get areResultsLoading$(): Observable<boolean> {
    return this._areResultsLoading$;
  }

  protected _campaigns$: BehaviorSubject<SurveySummaryModel[] | null>;
  get campaigns$(): Observable<SurveySummaryModel[] | null> {
    return this._campaigns$;
  }

  protected _selectedCampaign$: BehaviorSubject<SurveySummaryModel | null>;
  get selectedCampaign$(): Observable<SurveySummaryModel | null> {
    return this._selectedCampaign$;
  }

  protected _campaignsLoading$: BehaviorSubject<boolean>;
  get campaignsLoading$(): Observable<boolean> {
    return this._campaignsLoading$;
  }

  protected _showCompareResultsModal$: BehaviorSubject<boolean>;
  get showCompareResultsModal$(): Observable<boolean> {
    return this._showCompareResultsModal$;
  }

  protected _surveyIdentifierForComparison$: Subject<string> = new Subject<string>();

  private _surveysSubscription: Subscription = Subscription.EMPTY;

  constructor(
    protected _surveyService: SurveyBaseService
  ) {
    this._surveyResults$ =
      new BehaviorSubject<TrackMyJourneyResultsModel[] | null>(null);
    this._widgetDisplayMode$ = new BehaviorSubject<ResultsViews>(
      ResultsViews.chart
    );
    this._areResultsLoading$ = new BehaviorSubject<boolean>(true);
    this._showCompareResultsModal$ = new BehaviorSubject<boolean>(false);
    this._campaigns$ = new BehaviorSubject<SurveySummaryModel[] | null>(null);
    this._campaignsLoading$ = new BehaviorSubject<boolean>(false);
    this._selectedCampaign$ = new BehaviorSubject<SurveySummaryModel | null>(null);

    this._surveysSubscription = combineLatest([this._surveyIdentifierForComparison$, this._surveyService.selfSurveys$])
      .subscribe(([identifier, surveys]) => {
        const campaigns = surveys?.filter(s =>
          s.resultsStatus === ResultsStatuses.resultsAvailable &&
          s.status === SurveyStatuses.completeSurvey &&
          s.id !== identifier
        ) ?? new Array<SurveySummaryModel>;
      this._campaigns$.next(campaigns);
    })
  }

  ngOnDestroy(): void {
    if (this._surveysSubscription !== Subscription.EMPTY) {
      this._surveysSubscription.unsubscribe();
      this._surveysSubscription = Subscription.EMPTY;
    }
  }

  abstract compareResults(
    primaryIdentifier: string,
    secondaryIdentifier: string
  ): void;
  abstract loadCampaigns(identifier: string): void;

  setWidgetDisplayMode(mode: ResultsViews): void {
    this._widgetDisplayMode$.next(mode);
  }

  setResultsLoading(isLoading: boolean) {
    this._areResultsLoading$.next(isLoading);
  }

  setCampaignsLoading(isLoading: boolean) {
    this._campaignsLoading$.next(isLoading);
  }

  toggleCompareResultsModal(show: boolean) {
    this._showCompareResultsModal$.next(show);
  }

  selectCampaign(campaign: SurveySummaryModel) {
    this._selectedCampaign$.next(campaign);
  }

  setInitialResults(survey: SurveySummaryModel, results: SurveyResultsModel) {
    if (this._surveyResults$.value) {
      return;
    }
    const primaryResults: TrackMyJourneyResultsModel = {
      surveyResults: results,
      scoreList: this._mapScores(results),
      survey,
    };
    const secondaryResults: TrackMyJourneyResultsModel = {
      surveyResults: results,
      scoreList: this._mapScores(results),
      survey,
      disabled: true,
    };
    this._surveyResults$.next([primaryResults, secondaryResults]);
    this._areResultsLoading$.next(false);
  }

  protected _mapScores(results: SurveyResultsModel): ScoreModels {
    if (!results) {
      return {} as ScoreModels;
    }

    // sort results
    if (
      results.benchmark &&
      results.benchmark.fear &&
      results.benchmark.humility &&
      results.benchmark.love &&
      results.benchmark.pride
    ) {
      results.benchmark.fear.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.humility.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.love.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.pride.indicators.sort((a, b) => a.index - b.index);
    }

    if (
      results.self &&
      results.self.fear &&
      results.self.humility &&
      results.self.love &&
      results.self.pride
    ) {
      results.self.fear.indicators.sort((a, b) => a.index - b.index);
      results.self.humility.indicators.sort((a, b) => a.index - b.index);
      results.self.love.indicators.sort((a, b) => a.index - b.index);
      results.self.pride.indicators.sort((a, b) => a.index - b.index);
    }

    if (
      results.others &&
      results.others.fear &&
      results.others.humility &&
      results.others.love &&
      results.others.pride
    ) {
      results.others.fear.indicators.sort((a, b) => a.index - b.index);
      results.others.humility.indicators.sort((a, b) => a.index - b.index);
      results.others.love.indicators.sort((a, b) => a.index - b.index);
      results.others.pride.indicators.sort((a, b) => a.index - b.index);
    }

    const models = {} as ScoreModels;
    models.benchmark = new Array<ScoreModel[]>();
    models.self = new Array<ScoreModel[]>();
    models.others = new Array<ScoreModel[]>();

    if (results.benchmark) {
      for (let i = 0; i < 4; i++) {
        models.benchmark.push(new Array<ScoreModel>());
        models.benchmark[i].push({
          categoryName: results.benchmark.humility.indicators[i].categoryName,
          categoryType: results.benchmark.humility.indicators[i].categoryType,
          quadrantName: 'humility',
          score: results.benchmark.humility.indicators[i].score,
          index: results.benchmark.humility.indicators[i].index,
        } as ScoreModel);
        models.benchmark[i].push({
          categoryName: results.benchmark.love.indicators[i].categoryName,
          categoryType: results.benchmark.love.indicators[i].categoryType,
          quadrantName: 'love',
          score: results.benchmark.love.indicators[i].score,
          index: results.benchmark.love.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.benchmark) {
      for (let i = 0; i < 4; i++) {
        models.benchmark.push(new Array<ScoreModel>());
        models.benchmark[i + 4].push({
          categoryName: results.benchmark.pride.indicators[i].categoryName,
          categoryType: results.benchmark.pride.indicators[i].categoryType,
          quadrantName: 'pride',
          score: results.benchmark.pride.indicators[i].score,
          index: results.benchmark.pride.indicators[i].index,
        } as ScoreModel);
        models.benchmark[i + 4].push({
          categoryName: results.benchmark.fear.indicators[i].categoryName,
          categoryType: results.benchmark.fear.indicators[i].categoryType,
          quadrantName: 'fear',
          score: results.benchmark.fear.indicators[i].score,
          index: results.benchmark.fear.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.self) {
      for (let i = 0; i < 4; i++) {
        models.self.push(new Array<ScoreModel>());
        models.self[i].push({
          categoryName: results.self.humility.indicators[i].categoryName,
          categoryType: results.self.humility.indicators[i].categoryType,
          quadrantName: 'humility',
          score: results.self.humility.indicators[i].score,
          index: results.self.humility.indicators[i].index,
        } as ScoreModel);
        models.self[i].push({
          categoryName: results.self.love.indicators[i].categoryName,
          categoryType: results.self.love.indicators[i].categoryType,
          quadrantName: 'love',
          score: results.self.love.indicators[i].score,
          index: results.self.love.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.self) {
      for (let i = 0; i < 4; i++) {
        models.self.push(new Array<ScoreModel>());
        models.self[i + 4].push({
          categoryName: results.self.pride.indicators[i].categoryName,
          categoryType: results.self.pride.indicators[i].categoryType,
          quadrantName: 'pride',
          score: results.self.pride.indicators[i].score,
          index: results.self.pride.indicators[i].index,
        } as ScoreModel);
        models.self[i + 4].push({
          categoryName: results.self.fear.indicators[i].categoryName,
          categoryType: results.self.fear.indicators[i].categoryType,
          quadrantName: 'fear',
          score: results.self.fear.indicators[i].score,
          index: results.self.fear.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.others) {
      for (let i = 0; i < 4; i++) {
        if (
          results.others.humility.indicators.length === 0 ||
          results.others.love.indicators.length === 0
        ) {
          break;
        }
        models.others.push(new Array<ScoreModel>());
        models.others[i].push({
          categoryName: results.others.humility.indicators[i].categoryName,
          categoryType: results.others.humility.indicators[i].categoryType,
          quadrantName: 'humility',
          score: results.others.humility.indicators[i].score,
          index: results.others.humility.indicators[i].index,
        } as ScoreModel);
        models.others[i].push({
          categoryName: results.others.love.indicators[i].categoryName,
          categoryType: results.others.love.indicators[i].categoryType,
          quadrantName: 'love',
          score: results.others.love.indicators[i].score,
          index: results.others.love.indicators[i].index,
        } as ScoreModel);
      }
    }

    if (results.others) {
      for (let i = 0; i < 4; i++) {
        if (
          results.others.pride.indicators.length === 0 ||
          results.others.fear.indicators.length === 0
        ) {
          break;
        }
        models.others.push(new Array<ScoreModel>());
        models.others[i + 4].push({
          categoryName: results.others.pride.indicators[i].categoryName,
          categoryType: results.others.pride.indicators[i].categoryType,
          quadrantName: 'pride',
          score: results.others.pride.indicators[i].score,
          index: results.others.pride.indicators[i].index,
        } as ScoreModel);
        models.others[i + 4].push({
          categoryName: results.others.fear.indicators[i].categoryName,
          categoryType: results.others.fear.indicators[i].categoryType,
          quadrantName: 'fear',
          score: results.others.fear.indicators[i].score,
          index: results.others.fear.indicators[i].index,
        } as ScoreModel);
      }
    }

    return models;
  }
}
